import React, { useMemo } from 'react';
import { SearchableList } from '@ds';
import differenceBy from 'lodash/differenceBy';
import WorkspaceSelectItem from './WorkspaceSelectItem';
import { useActiveWorkspaceName, useCurrentOrganization } from '@shared/hooks';
import { noop } from 'lodash';
import { Workspace } from '@shared/types';
import workspaceActions from '@/actions/workspaceActions';
import { sortArrayOfObjectsByKey } from '@/helpers/generalHelpers';
import { useDispatch } from 'react-redux';
import useWorkspacesMemberInOrganization from '@shared/hooks/workspace/useWorkspacesMemberInOrganization';
import useWorkspacesPublicInOrganization from '@shared/hooks/workspace/useWorkspacesPublicInOrganization';
import useOrganizationWorkspaces from '@shared/api/useOrganizationWorkspaces';

type UserMenuWorkspacesSelectorProps = {
  onChange?: (workspaceName: string) => void;
  onViewAll?: () => void;
  dataTestPrefix?: string;
};

const UserMenuWorkspacesSelector: React.FC<UserMenuWorkspacesSelectorProps> = ({
  onChange = noop,
  onViewAll = noop,
  dataTestPrefix = ''
}) => {
  const dispatch = useDispatch();
  const currentOrganization = useCurrentOrganization();
  const currentWorkspaceName = useActiveWorkspaceName();
  const { data: memberWorkspaces = [] } = useWorkspacesMemberInOrganization();
  const { data: publicWorkspaces = [] } = useWorkspacesPublicInOrganization();
  const { data: orgWorkspaces = [] } = useOrganizationWorkspaces(
    currentOrganization?.id,
    {
      enabled: !!currentOrganization?.isAdmin
    }
  );

  const onSelectWorkspace = (workspaceName: string) => {
    dispatch(workspaceActions.switchTeam(workspaceName));
  };

  const workspaceList = useMemo(() => {
    if (currentOrganization?.isAdmin) {
      return [
        {
          title: 'My workspaces',
          itemList: sortArrayOfObjectsByKey(memberWorkspaces, 'name')
        },
        {
          title: 'Other workspaces',
          itemList: sortArrayOfObjectsByKey(
            differenceBy(orgWorkspaces, memberWorkspaces, 'id'),
            'name'
          )
        }
      ];
    }

    return [
      {
        title: 'My workspaces',
        itemList: sortArrayOfObjectsByKey(memberWorkspaces, 'name')
      },
      {
        title: 'Other workspaces',
        itemList: sortArrayOfObjectsByKey(publicWorkspaces, 'name'),
        infoText: 'Read-only access to public projects, models or artifacts.'
      }
    ];
  }, [
    currentOrganization?.isAdmin,
    orgWorkspaces,
    memberWorkspaces,
    publicWorkspaces
  ]);

  const handleSelectWorkspace = (workspaceName: string) => {
    onSelectWorkspace(workspaceName);
    onChange(workspaceName);
  };

  return (
    <SearchableList itemList={workspaceList} searchField="name">
      <SearchableList.Result maxHeight={320}>
        {(workspace: Workspace) => (
          <WorkspaceSelectItem
            key={workspace.id}
            workspace={workspace}
            currentWorkspaceName={currentWorkspaceName}
            onSelect={handleSelectWorkspace}
            dataTestPrefix={dataTestPrefix}
          />
        )}
      </SearchableList.Result>
      <SearchableList.NotFound />
      <SearchableList.Footer>
        <SearchableList.FooterActionLink
          text="View all workspaces"
          to="/account-settings/workspaces"
          onClick={onViewAll}
        />
      </SearchableList.Footer>
    </SearchableList>
  );
};

export default UserMenuWorkspacesSelector;
